import { DaySelector } from '@common/components/DaySelector';
import {
  ExperienceStepSchema,
  PackagesFormValues,
  PackageType,
} from '@features/DynamicPackagesConfigurationWizard/types';
import { useGetSpaPackage } from '@features/spa';
import { useTranslations } from '@lang';
import moment from 'moment';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { SpaTitleWrapper } from '../../../components/ExperiencesGroup/ExperiencesGroup.styles';
import { usePackageConfiguration } from '../../../contexts';
import { PageTitle } from '../../FormSteps.styles';
import { useGroupOptionsByDateAndOptionGroup } from '../../hooks/useGroupOptionsByDate';
import { useHashNavigation } from '../../hooks/useHashNavigation';
import { useStepFooter } from '../../hooks/useStepFooter';

import { DayContainer } from '@features/DynamicPackagesConfigurationWizard/components/AvailabilityForm/AvailabilityForm.styles';
import { useExperienceSameTimes } from '../../../hooks/useExperienceSameTimes';
import { SpaSectionWrapper } from '../ExperiencesStep.styles';
import { useCheckSpaDayIsAvailable } from './hooks/useCheckSpaDayIsAvailable';
import { useDateGroupSelection } from './hooks/useDateGroupSelection';
import { MultipleOptionsExperienceTimeslotSelection } from './MultipleOptionsExperienceTimeslotSelection/MultipleOptionsExperienceTimeslotSelection';
import {
  ExperienceTitleWrapper,
  ExperienceWrapper,
  SpaEntitySelectionWrapper,
  SpaEntityStepWrapper,
} from './RepeatingExperiencesStep.styles';
import { SingleOptionExperienceTimeslotSelection } from './SingleOptionExperienceTimeslotSelection/SingleOptionExperienceTimeslotSelection';
import type { ExperiencesByDay } from './types';

export const RepeatingExperiencesTimeslotsStep = () => {
  const {
    property,
    journeyParams,
    spaPackageId,
    stayDuration,
    totalGuestAmount,
    spaOpenTime,
    spaCloseTime,
  } = usePackageConfiguration();

  const numberOfGuests = totalGuestAmount;

  useHashNavigation({ hash: 'experiencesTimeslotsStep' });

  const { t } = useTranslations();
  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext<PackagesFormValues>();
  const selectedExperiences = watch('selectedExperiences');
  const guests = watch('guests') || [];

  const { experienceSameTimes, toggleExperienceSameTime, canEnableMultiGuestSameTime } =
    useExperienceSameTimes({
      selectedExperiences,
      numberOfGuests,
      defaultValue: true,
      queryClient,
      propertyId: Number(property.id),
      spaPackageId: Number(spaPackageId),
      spaOpenTime,
      spaCloseTime,
    });

  const { data: spaDay } = useGetSpaPackage(
    Number(property.id),
    Number(spaPackageId),
    property.defaultLanguage,
    true
  );

  const selectedExperiencesWithoutExtras = (selectedExperiences || []).filter(
    (exp) => exp.type !== 'spa-extras'
  );

  // Repeating packages: PackageType.REPEATING
  const selectedExperiencesByDay: ExperiencesByDay[] = useGroupOptionsByDateAndOptionGroup(
    moment(journeyParams.fromDate),
    selectedExperiencesWithoutExtras,
    PackageType.REPEATING,
    stayDuration,
    numberOfGuests,
    spaDay?.configurationGroups
  );

  // Get all possible dates across the stay
  const allPossibleDates = Array.from({ length: stayDuration + 1 }, (_, i) =>
    moment(journeyParams.fromDate).add(i, 'days').format('YYYY-MM-DD')
  );

  const { availableDates: spaAvailableDates } = useCheckSpaDayIsAvailable({
    propertyId: property.id,
    spaPackageId: Number(spaPackageId),
    datesToCheck: allPossibleDates,
    numberOfGuests,
    enabled: true,
  });

  const { dateGroupData, handleDayChange } = useDateGroupSelection({
    selectedExperiencesByDay,
    journeyParams,
    guests,
    setValue,
    spaAvailableDates,
  });

  const totalRequiredBookings = selectedExperiencesByDay.reduce((count, day) => {
    return (
      count +
      day.options.reduce((optCount, opt) => {
        if (!opt.options?.length) return optCount;
        const cap = opt.maximumSelections
          ? Math.min(opt.maximumSelections, opt.options.length)
          : opt.options.length;
        return optCount + cap;
      }, 0)
    );
  }, 0);

  const totalTimeSlotsRequired = totalRequiredBookings * numberOfGuests;

  const totalValidTimeslots = guests.reduce((total, guest) => {
    const experiences = guest.experiences || [];
    if (ExperienceStepSchema.safeParse(experiences).success) {
      const validExperiences = experiences.filter((b) => !!b && b.startTime);
      return total + validExperiences.length;
    }
    return total;
  }, 0);

  const isValid = totalValidTimeslots === totalTimeSlotsRequired;

  const footer = useStepFooter({ isValid });

  return (
    <>
      <ExperienceWrapper>
        <ExperienceTitleWrapper>
          <SpaTitleWrapper>
            <PageTitle>Spa</PageTitle>
          </SpaTitleWrapper>
          <h2 data-cy="page-title">{t('spa.package.packageAvailabilityTitle')}</h2>
        </ExperienceTitleWrapper>
        <SpaSectionWrapper>
          <SpaEntityStepWrapper>
            {dateGroupData.map((group) => {
              const { date, experiences, selectedDay, availableDates } = group;
              const currentlySelectedDate = selectedDay
                ? moment(journeyParams.fromDate).add(selectedDay, 'days').format('YYYY-MM-DD')
                : availableDates[0];

              return (
                <div key={`date-group-${date}`}>
                  {experiences.length > 0 && (
                    <>
                      <DayContainer>
                        <DaySelector
                          availableDates={availableDates}
                          startDate={journeyParams.fromDate}
                          endDate={journeyParams.toDate || journeyParams.fromDate}
                          currentSelection={currentlySelectedDate}
                          onChange={(dateString) => handleDayChange(date, dateString)}
                        />
                      </DayContainer>

                      {experiences.map((experience) => {
                        const { key, type, data, experienceIndex } = experience;
                        const isSameTime = experienceSameTimes[experienceIndex] ?? false;
                        const optionIds =
                          type === 'group'
                            ? data.options.map((opt) => Number(opt.optionId))
                            : [Number(data.optionId)];
                        const canToggleSameTime = canEnableMultiGuestSameTime(
                          currentlySelectedDate,
                          optionIds
                        );

                        if (type === 'group') {
                          return (
                            <SpaEntitySelectionWrapper key={key}>
                              {isSameTime ? (
                                <MultipleOptionsExperienceTimeslotSelection
                                  experiences={data}
                                  experienceIndex={experienceIndex}
                                  configurationGroups={spaDay?.configurationGroups || []}
                                  guestIndex={0}
                                  selectedDayNumber={selectedDay}
                                  numberOfGuests={numberOfGuests}
                                  isSameTimeForAllGuests={true}
                                  onToggleSameTime={
                                    canToggleSameTime
                                      ? () => toggleExperienceSameTime(experienceIndex)
                                      : undefined
                                  }
                                />
                              ) : (
                                Array.from({ length: numberOfGuests }, (_, guestIndex) => (
                                  <MultipleOptionsExperienceTimeslotSelection
                                    key={`guest-${guestIndex}`}
                                    experiences={data}
                                    experienceIndex={experienceIndex}
                                    configurationGroups={spaDay?.configurationGroups || []}
                                    guestIndex={guestIndex}
                                    selectedDayNumber={selectedDay}
                                    numberOfGuests={numberOfGuests}
                                    isSameTimeForAllGuests={false}
                                    onToggleSameTime={
                                      guestIndex === 0 && canToggleSameTime
                                        ? () => toggleExperienceSameTime(experienceIndex)
                                        : undefined
                                    }
                                  />
                                ))
                              )}
                            </SpaEntitySelectionWrapper>
                          );
                        } else {
                          return (
                            <SpaEntitySelectionWrapper key={key}>
                              {isSameTime ? (
                                <SingleOptionExperienceTimeslotSelection
                                  experience={data}
                                  experienceIndex={experienceIndex}
                                  configurationGroups={spaDay?.configurationGroups || []}
                                  guestIndex={0}
                                  selectedDayNumber={selectedDay}
                                  numberOfGuests={numberOfGuests}
                                  isSameTimeForAllGuests={true}
                                  onToggleSameTime={
                                    canToggleSameTime
                                      ? () => toggleExperienceSameTime(experienceIndex)
                                      : undefined
                                  }
                                />
                              ) : (
                                Array.from({ length: numberOfGuests }, (_, guestIndex) => (
                                  <SingleOptionExperienceTimeslotSelection
                                    key={`guest-${guestIndex}`}
                                    experience={data}
                                    experienceIndex={experienceIndex}
                                    configurationGroups={spaDay?.configurationGroups || []}
                                    guestIndex={guestIndex}
                                    selectedDayNumber={selectedDay}
                                    numberOfGuests={numberOfGuests}
                                    isSameTimeForAllGuests={false}
                                    onToggleSameTime={
                                      guestIndex === 0 && canToggleSameTime
                                        ? () => toggleExperienceSameTime(experienceIndex)
                                        : undefined
                                    }
                                  />
                                ))
                              )}
                            </SpaEntitySelectionWrapper>
                          );
                        }
                      })}
                    </>
                  )}
                </div>
              );
            })}
          </SpaEntityStepWrapper>
        </SpaSectionWrapper>
      </ExperienceWrapper>
      {footer}
    </>
  );
};
